<template>
  <div class="pesquisa-menu" @keydown="handleKeydown">
    <v-text-field
      ref="searchField"
      :id="`${searchId}-input`"
      :value="procurar"
      :placeholder="$t('Pesquisa_menu_placeholder')"
      :aria-label="$t('Pesquisa_menu_placeholder')"
      :aria-controls="hasSearch ? `${searchId}-results` : undefined"
      :aria-describedby="hasSearch ? `${searchId}-status` : undefined"
      role="searchbox"
      autocomplete="off"
      autocapitalize="off"
      :spellcheck="false"
      rounded-sm
      hide-details
      outlined
      prepend-inner-icon="mdi-magnify"
      background-color="rgba(255,255,255,.9)"
      color="rgba(0,0,0,.6)"
      light
      class="
        font-size-input
        placeholder-lighter
        input-alternative input-icon
        pesquisa-menu__field
      "
      @input="updateSearch"
    >
      <template v-slot:append>
        <v-btn
          v-if="procurar.length"
          icon
          small
          class="pesquisa-menu__clear"
          :aria-label="$t('Pesquisa_menu_limpar')"
          :title="$t('Pesquisa_menu_limpar')"
          @click="clearSearch"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-text-field>

    <div
      :id="`${searchId}-status`"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      :class="hasSearch ? 'pesquisa-menu__status' : 'pesquisa-menu__sr-only'"
    >
      {{ resultStatus }}
    </div>

    <section
      v-if="hasSearch"
      :id="`${searchId}-results`"
      :aria-label="$t('Pesquisa_menu_resultados')"
      class="pesquisa-menu__results"
    >
      <template v-if="itensFiltrados.length">
        <div
          ref="resultsScroller"
          class="pesquisa-menu__results-scroll"
          @scroll="updateResultsScrollState"
        >
          <v-list dense class="py-1" role="presentation">
            <template v-for="(item, i) in itensFiltrados">
              <v-list-item
                :key="item.searchKey"
                :to="item.link"
                exact
                role="link"
                class="pesquisa-menu__result"
                :title="`${item.label} — ${item.contextLabel}`"
                @click="selectItem"
              >
                <v-list-item-icon class="pesquisa-menu__result-icon">
                  <v-icon>{{ item.prefix || "mdi-arrow-right" }}</v-icon>
                </v-list-item-icon>

                <v-list-item-content class="pesquisa-menu__result-content">
                  <v-list-item-title class="pesquisa-menu__result-title">
                    <template
                      v-for="(part, partIndex) in highlight(item.label)"
                    >
                      <mark v-if="part.match" :key="partIndex">{{
                        part.text
                      }}</mark>
                      <span v-else :key="partIndex">{{ part.text }}</span>
                    </template>
                  </v-list-item-title>
                  <v-list-item-subtitle
                    v-if="item.contextLabel"
                    class="pesquisa-menu__result-context"
                  >
                    <template
                      v-for="(part, partIndex) in highlight(item.contextLabel)"
                    >
                      <mark v-if="part.match" :key="partIndex">{{
                        part.text
                      }}</mark>
                      <span v-else :key="partIndex">{{ part.text }}</span>
                    </template>
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-icon class="pesquisa-menu__result-arrow" small>
                  mdi-chevron-right
                </v-icon>
              </v-list-item>
              <v-divider
                v-if="i < itensFiltrados.length - 1"
                :key="`divider-${item.searchKey}`"
                class="pesquisa-menu__divider"
              ></v-divider>
            </template>
          </v-list>
        </div>
        <div
          v-if="showScrollHint"
          aria-hidden="true"
          class="pesquisa-menu__scroll-hint"
        >
          <v-icon small>mdi-chevron-down</v-icon>
        </div>
      </template>
      <div v-else class="pesquisa-menu__empty">
        <v-icon aria-hidden="true" class="mb-2">mdi-magnify-close</v-icon>
        <p>{{ $t("Pesquisa_menu_sem_resultados_dica") }}</p>
        <v-btn
          text
          small
          class="pesquisa-menu__empty-clear"
          @click="clearSearch"
        >
          {{ $t("Pesquisa_menu_limpar") }}
        </v-btn>
      </div>
    </section>
    <p
      v-if="hasSearch && itensFiltrados.length"
      class="pesquisa-menu__keyboard-hint"
    >
      {{ $t("Pesquisa_menu_teclado") }}
    </p>
  </div>
</template>

<script>
import { hasModulo, hasPermission } from "@/utils/menuNavigation";
import {
  createMenuSearchIndex,
  highlightMenuSearchText,
  normalizeMenuSearchText,
  searchMenuItems,
} from "@/utils/menuSearch";

export default {
  name: "PesquisaMenu",

  props: {
    payload: { type: Array, required: true },
    active: { type: Boolean, default: true },
  },

  data() {
    return {
      procurar: "",
      resultsAtBottom: true,
      resultsScrollable: false,
    };
  },

  computed: {
    searchId() {
      return `pesquisa-menu-${this._uid}`;
    },

    hasSearch() {
      return normalizeMenuSearchText(this.procurar).length > 0;
    },

    searchIndex() {
      return createMenuSearchIndex(this.payload, {
        translate: (title) => this.$t(title),
        isAvailable: (item) =>
          hasPermission(item.can, this.$store.state.permissoes) &&
          hasModulo(item.modulo, this.$store.state),
      });
    },

    itensFiltrados() {
      return searchMenuItems(this.searchIndex, this.procurar);
    },

    resultStatus() {
      if (!this.hasSearch) return "";
      const count = this.itensFiltrados.length;
      if (!count) return this.$t("Nenhum_resultado_encontrado");
      return this.$tc("Pesquisa_menu_contagem", count, { count });
    },

    showScrollHint() {
      return this.resultsScrollable && !this.resultsAtBottom;
    },
  },

  methods: {
    updateSearch(value) {
      this.procurar = value == null ? "" : String(value);
    },

    resetSearch() {
      this.procurar = "";
      this.resultsScrollable = false;
      this.resultsAtBottom = true;
    },

    clearSearch() {
      this.resetSearch();
      this.$nextTick(() => this.$refs.searchField?.focus());
    },

    highlight(text) {
      return highlightMenuSearchText(text, this.procurar);
    },

    getResultElements() {
      return Array.from(
        this.$refs.resultsScroller?.querySelectorAll(
          ".pesquisa-menu__result"
        ) || []
      );
    },

    // Links reais mantêm Tab, Enter, Ctrl/Cmd+clique e abrir em nova aba.
    handleKeydown(event) {
      if (event.isComposing || event.keyCode === 229) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
        return;

      if (event.key === "Escape" && this.procurar) {
        event.preventDefault();
        event.stopPropagation();
        this.clearSearch();
        return;
      }

      const results = this.getResultElements();
      if (!results.length) return;
      const fromInput = event.target === this.$refs.searchField?.$refs.input;
      const index = results.indexOf(
        event.target.closest(".pesquisa-menu__result")
      );
      if (!fromInput && index === -1) return;

      if (event.key === "Enter") {
        event.preventDefault();
        results[fromInput ? 0 : index].click();
        return;
      }

      let next;
      if (event.key === "ArrowDown") next = fromInput ? 0 : index + 1;
      else if (event.key === "ArrowUp") {
        if (!fromInput && index === 0) {
          event.preventDefault();
          this.$refs.searchField.focus();
          return;
        }
        next = fromInput ? results.length - 1 : index - 1;
      } else if (!fromInput && event.key === "Home") next = 0;
      else if (!fromInput && event.key === "End") next = results.length - 1;
      else return;

      event.preventDefault();
      const target = results[Math.min(next, results.length - 1)];
      target.focus({ preventScroll: true });
      target.scrollIntoView({ block: "nearest" });
    },

    resetResultsScroll() {
      this.$nextTick(() => {
        const scroller = this.$refs.resultsScroller;
        if (scroller) scroller.scrollTop = 0;
        this.updateResultsScrollState();
      });
    },

    updateResultsScrollState() {
      const scroller = this.$refs.resultsScroller;
      if (!scroller) {
        this.resultsScrollable = false;
        this.resultsAtBottom = true;
        return;
      }

      const maxScroll = scroller.scrollHeight - scroller.clientHeight;
      this.resultsScrollable = maxScroll > 4;
      this.resultsAtBottom =
        !this.resultsScrollable || scroller.scrollTop >= maxScroll - 4;
    },

    selectItem(event) {
      // O Vuetify também emite click no keydown; a navegação usa o clique real.
      if (event?.type === "keydown") return;
      // Uma nova aba não deve fechar nem limpar a pesquisa na aba atual.
      if (
        event &&
        (event.ctrlKey ||
          event.metaKey ||
          event.shiftKey ||
          event.altKey ||
          event.button > 0)
      )
        return;
      this.resetSearch();
      this.$emit("navigate");
    },
  },

  mounted() {
    window.addEventListener("resize", this.updateResultsScrollState);
    if (typeof ResizeObserver !== "undefined") {
      this.resultsResizeObserver = new ResizeObserver(
        this.updateResultsScrollState
      );
      this.resultsResizeObserver.observe(this.$el);
    }
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.updateResultsScrollState);
    this.resultsResizeObserver?.disconnect();
  },

  watch: {
    itensFiltrados() {
      this.resetResultsScroll();
    },

    active(value) {
      if (!value) this.resetSearch();
    },

    "$route.fullPath"() {
      this.resetSearch();
    },
  },
};
</script>

<style scoped>
.pesquisa-menu {
  padding: 0 16px;
  position: relative;
  text-align: left;
}

.pesquisa-menu__field {
  max-width: 100%;
}

.pesquisa-menu__field ::v-deep input {
  min-width: 0;
}

.pesquisa-menu__field ::v-deep .v-input__append-inner {
  align-self: center;
  margin-top: 0;
}

.pesquisa-menu__clear {
  margin: 0;
}

.pesquisa-menu__status {
  color: #67748e;
  font-size: 0.76rem;
  font-weight: 600;
  padding: 10px 2px 6px;
}

.pesquisa-menu__sr-only {
  clip: rect(0, 0, 0, 0);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.pesquisa-menu__results {
  border: 1px solid rgba(103, 116, 142, 0.14);
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(20, 20, 43, 0.12);
  overflow: hidden;
  position: relative;
}

.pesquisa-menu__results-scroll {
  max-height: 42vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-bottom: 10px;
  scrollbar-color: rgba(234, 6, 6, 0.42) rgba(103, 116, 142, 0.12);
  scrollbar-width: thin;
}

.pesquisa-menu__results-scroll::-webkit-scrollbar {
  width: 8px;
}

.pesquisa-menu__results-scroll::-webkit-scrollbar-track {
  background: rgba(103, 116, 142, 0.1);
  border-radius: 999px;
}

.pesquisa-menu__results-scroll::-webkit-scrollbar-thumb {
  background: rgba(234, 6, 6, 0.42);
  border: 2px solid #fff;
  border-radius: 999px;
}

.pesquisa-menu__scroll-hint {
  align-items: flex-end;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.94) 58%,
    #fff 100%
  );
  bottom: 0;
  display: flex;
  height: 42px;
  justify-content: center;
  left: 0;
  padding-bottom: 7px;
  pointer-events: none;
  position: absolute;
  right: 0;
}

.pesquisa-menu__scroll-hint .v-icon {
  background: #fff;
  border: 1px solid rgba(103, 116, 142, 0.16);
  border-radius: 999px;
  box-shadow: 0 6px 14px rgba(20, 20, 43, 0.12);
  color: #ea0606;
  height: 24px;
  width: 24px;
}

.pesquisa-menu__result-icon {
  align-self: center;
  background: rgba(234, 6, 6, 0.08);
  border-radius: 6px;
  height: 30px;
  justify-content: center;
  margin: 8px 12px 8px 0;
  min-width: 30px;
  width: 30px;
}

.pesquisa-menu__result-icon .v-icon {
  color: #ea0606;
  font-size: 18px;
}

.pesquisa-menu__result {
  min-height: 56px;
  padding: 6px 12px;
}

.pesquisa-menu__result:hover {
  background: #f8f9fa;
}

.pesquisa-menu__result:focus {
  background: #fff1f0;
  outline: 2px solid #ea0606;
  outline-offset: -2px;
}

.pesquisa-menu__result-content {
  min-width: 0;
  padding: 4px 0;
}

.pesquisa-menu__result-title {
  color: #344767;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.25rem;
}

.pesquisa-menu__result-context {
  color: #67748e !important;
  font-size: 0.74rem;
  line-height: 1rem;
  margin-top: 2px;
  overflow-wrap: anywhere;
  white-space: normal;
}

.pesquisa-menu mark {
  background: rgba(234, 6, 6, 0.1);
  border-radius: 2px;
  color: inherit;
  font-weight: 700;
  padding: 0;
}

.pesquisa-menu__result-arrow {
  color: #a0aec0;
  margin-left: 8px;
}

.pesquisa-menu__divider {
  margin: 0 12px 0 54px;
}

.pesquisa-menu__empty {
  color: #67748e;
  font-size: 0.85rem;
  padding: 16px 12px 12px;
  text-align: center;
}

.pesquisa-menu__empty p {
  font-size: inherit;
  margin-bottom: 8px;
}

.pesquisa-menu__empty-clear {
  color: #b92121;
  letter-spacing: normal;
  text-transform: none;
}

.pesquisa-menu__keyboard-hint {
  color: #67748e;
  font-size: 0.7rem;
  line-height: 1.4;
  margin: 8px 0 0;
}

@media (max-width: 600px) {
  .pesquisa-menu {
    padding: 0 12px;
  }

  .pesquisa-menu__results-scroll {
    max-height: 36vh;
  }
}
</style>
